// Confirmación previa a un cambio de estado de una política especial. Vencer y eliminar son
// irreversibles. Pausar, vencer y eliminar piden motivo; en eliminar es obligatorio porque el
// lambda responde 422 sin él.
import { useState } from 'react';
import { Button, Modal } from '@/kit';
import { actionCopy, actionRequiresReason, actionTakesReason } from '../logic';
import type { PolicyAction, SpecialPolicy } from '../types';
import { FIELD_INPUT, FIELD_LABEL } from './ui';

export function PolicyStatusDialog({
    action,
    policy,
    saving,
    onCancel,
    onConfirm,
}: {
    action: PolicyAction;
    policy: SpecialPolicy;
    saving: boolean;
    onCancel: () => void;
    onConfirm: (reason?: string) => void;
}) {
    const copy = actionCopy(action);
    const [reason, setReason] = useState('');
    const takesReason = actionTakesReason(action);
    const reasonRequired = actionRequiresReason(action);
    const trimmedReason = reason.trim();
    const missingReason = reasonRequired && trimmedReason === '';

    return (
        <Modal isOpen onClose={onCancel} title={copy.title} description={policy.name}>
            <p className="text-[12px] text-muted-foreground">{copy.description}</p>
            {takesReason ? (
                <label className="mt-3 block">
                    <span className={FIELD_LABEL}>
                        {reasonRequired ? 'Motivo' : 'Motivo (opcional)'}
                    </span>
                    <textarea
                        rows={2}
                        className={FIELD_INPUT}
                        value={reason}
                        placeholder="ej. mora temprana arriba de lo esperado"
                        onChange={(event) => {
                            setReason(event.target.value);
                        }}
                    />
                    <span className="mt-1 block text-[10px] text-muted-foreground">
                        {reasonRequired
                            ? 'Es lo único que queda para explicar por qué desapareció de esta pantalla.'
                            : 'Queda escrito en la bitácora junto al evento.'}
                    </span>
                </label>
            ) : null}
            <div className="mt-4 flex justify-end gap-2">
                <Button variant="outline" onClick={onCancel} disabled={saving}>
                    Cancelar
                </Button>
                <Button
                    variant={copy.destructive ? 'destructive' : 'default'}
                    isLoading={saving}
                    // `aria-disabled` y no `disabled`: un control deshabilitado no anuncia por qué
                    // lo está, y el operador se queda sin saber que le falta el motivo.
                    aria-disabled={missingReason}
                    onClick={() => {
                        if (missingReason) return;
                        onConfirm(takesReason && trimmedReason !== '' ? trimmedReason : undefined);
                    }}
                >
                    {copy.confirmLabel}
                </Button>
            </div>
        </Modal>
    );
}
